<template>
  <div class="mt-3">
    <!-- Create form -->
    <div class="card flat-card mb-3">
      <div class="card-body p-3">
        <h6 class="section-title mb-3">
          <i class="bi bi-megaphone mr-1"></i> New Announcement
        </h6>
        <form @submit.prevent="submit">
          <div class="form-row">
            <div class="form-group col-md-8">
              <label class="font-weight-bold">Title</label>
              <input v-model="form.title" class="form-control" placeholder="Scheduled maintenance tonight…" required maxlength="150" />
            </div>
            <div class="form-group col-md-2">
              <label class="font-weight-bold">Type</label>
              <select v-model="form.type" class="form-control">
                <option value="banner">Banner</option>
                <option value="modal">Modal</option>
              </select>
            </div>
            <div class="form-group col-md-2">
              <label class="font-weight-bold">Level</label>
              <select v-model="form.level" class="form-control">
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="danger">Danger</option>
                <option value="success">Success</option>
              </select>
            </div>
          </div>
          <div class="form-group">
            <label class="font-weight-bold">Message</label>
            <textarea v-model="form.message" class="form-control" rows="2" maxlength="1000" required></textarea>
          </div>
          <div class="form-row">
            <div class="form-group col-md-3">
              <label class="font-weight-bold">Target</label>
              <select v-model="form.target_type" class="form-control">
                <option value="all">All merchants</option>
                <option value="plan">By plan</option>
                <option value="status">By status</option>
              </select>
            </div>
            <div v-if="form.target_type !== 'all'" class="form-group col-md-3">
              <label class="font-weight-bold">Value</label>
              <input v-model="form.target_value" class="form-control" :placeholder="form.target_type === 'plan' ? 'e.g. Starter' : 'e.g. active'" />
            </div>
            <div class="form-group col-md-3">
              <label class="font-weight-bold">Start (optional)</label>
              <input v-model="form.starts_at" type="datetime-local" class="form-control" />
            </div>
            <div class="form-group col-md-3">
              <label class="font-weight-bold">End (optional)</label>
              <input v-model="form.ends_at" type="datetime-local" class="form-control" />
            </div>
          </div>
          <button class="btn btn-primary" type="submit" :disabled="saving">
            <span v-if="saving" class="spinner-border spinner-border-sm mr-1"></span>
            Publish Announcement
          </button>
        </form>
      </div>
    </div>

    <!-- List -->
    <div class="card flat-card">
      <div class="card-body p-3">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="section-title mb-0">All Announcements</h6>
          <button class="btn btn-dark" @click="fetch" :disabled="loading">
            <i class="bi bi-arrow-clockwise"></i>
          </button>
        </div>

        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border text-secondary" role="status"></div>
        </div>

        <div v-else-if="announcements.length === 0" class="text-center text-secondary py-3">
          <small>No announcements yet</small>
        </div>

        <div v-else>
          <div
            v-for="ann in announcements"
            :key="ann.id"
            class="border rounded p-3 mb-2"
            :class="ann.active ? '' : 'bg-light'"
          >
            <div class="d-flex justify-content-between align-items-start">
              <div style="min-width:0;">
                <div class="d-flex align-items-center flex-wrap" style="gap:6px;">
                  <span :class="['badge', levelBadge(ann.level)]">{{ ann.level }}</span>
                  <span class="badge badge-secondary">{{ ann.type }}</span>
                  <span v-if="ann.target_type !== 'all'" class="badge badge-light border">
                    {{ ann.target_type }}: {{ ann.target_value }}
                  </span>
                  <span v-if="!ann.active" class="badge badge-secondary">Disabled</span>
                </div>
                <p class="font-weight-bold mb-0 mt-1" style="font-size:.85rem;">{{ ann.title }}</p>
                <p class="text-secondary mb-0" style="font-size:.8rem;">{{ ann.message }}</p>
                <small class="text-secondary" style="font-size:.72rem;">
                  By {{ ann.created_by }} · {{ fromNow(ann.created_at) }}
                  <span v-if="ann.ends_at"> · expires {{ fromNow(ann.ends_at) }}</span>
                </small>
              </div>
              <div class="d-flex flex-shrink-0 ml-2" style="gap:4px;">
                <button
                  class="btn"
                  :class="ann.active ? 'btn-outline-secondary' : 'btn-outline-primary'"
                  @click="toggle(ann)"
                  style="padding:2px 8px;"
                >
                  <i :class="ann.active ? 'bi bi-pause' : 'bi bi-play'"></i>
                </button>
                <button class="btn btn-outline-danger" @click="remove(ann.id)" style="padding:2px 8px;">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AdminAnnouncements',

  data() {
    return {
      loading: false,
      saving: false,
      announcements: [],
      form: {
        title: '',
        message: '',
        type: 'banner',
        level: 'info',
        target_type: 'all',
        target_value: '',
        starts_at: '',
        ends_at: '',
      },
    };
  },

  mounted() {
    this.fetch();
  },

  methods: {
    async fetch() {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/admin/announcements');
        this.announcements = data;
      } finally {
        this.loading = false;
      }
    },

    async submit() {
      this.saving = true;
      try {
        await axios.post('/api/admin/announcements', {
          ...this.form,
          starts_at: this.form.starts_at || null,
          ends_at: this.form.ends_at || null,
          target_value: this.form.target_type !== 'all' ? this.form.target_value : null,
        });
        this.$toasts.push({ message: 'Announcement published', type: 'success' });
        this.form = { title: '', message: '', type: 'banner', level: 'info', target_type: 'all', target_value: '', starts_at: '', ends_at: '' };
        await this.fetch();
      } catch (e) {
        this.$toasts.push({ message: e?.response?.data?.message || 'Failed to publish', type: 'error' });
      } finally {
        this.saving = false;
      }
    },

    async toggle(ann) {
      try {
        const { data } = await axios.post(`/api/admin/announcements/${ann.id}/toggle`);
        ann.active = data.active;
      } catch (e) {
        this.$toasts.push({ message: 'Failed to toggle', type: 'error' });
      }
    },

    async remove(id) {
      if (!confirm('Delete this announcement?')) return;
      try {
        await axios.delete(`/api/admin/announcements/${id}`);
        this.announcements = this.announcements.filter(a => a.id !== id);
        this.$toasts.push({ message: 'Announcement deleted', type: 'info' });
      } catch (e) {
        this.$toasts.push({ message: 'Failed to delete', type: 'error' });
      }
    },

    levelBadge(level) {
      return { info: 'badge-dark', warning: 'badge-danger', danger: 'badge-danger', success: 'badge-primary' }[level] || 'badge-secondary';
    },

    fromNow(ts) {
      return moment(ts).fromNow();
    },
  },
};
</script>

<style scoped>
.flat-card {
  border: 1px solid #e9ecef;
  border-radius: 14px;
}
.section-title {
  color: #0f172a;
  font-weight: 600;
}
</style>
